import { API, graphqlOperation } from "aws-amplify";

import { carrierIdentitiesByReloadYard } from "../graphql/carrier/carrier-queries";

import { logError } from "./appUtils";

/**
 * One place to decide whether two carrier records are the same company.
 *
 * Every carrier create path used to answer that question its own way: the
 * manager's Carriers form checked email against the server, the inline dialog
 * checked name, email and phone against whatever carriers its dropdown happened
 * to have loaded. Same yard, three different answers. The rules live here now,
 * and both paths call validateCarrierUnicity.
 *
 * The comparison runs on normalised values, so the near-misses that made the old
 * checks miss duplicates — a capitalised email, a phone typed with dashes one
 * time and spaces the next — resolve to the same identity.
 */

// How each field is named back to the user — the form's labels, not the
// schema's field names. An identifier names its own scheme instead, so it is
// resolved from CARRIER_ID_SCHEMES rather than listed here.
const CONFLICT_LABELS = {
  email: "email address",
  phone: "phone number",
  businessName: "business name",
};

/**
 * @param {string} email
 * @returns {string|null} the value stored in Carrier.normalizedEmail
 */
export const normalizeCarrierEmail = (email) =>
  String(email ?? "")
    .trim()
    .toLowerCase() || null;

/**
 * Reduce a phone number to the digits that identify the line.
 *
 * Carriers type the same number as (555) 123-4567, 555.123.4567 or
 * +1 555 123 4567, and a yard's book is North American, so a leading 1 on an
 * 11-digit number is the country code rather than part of the number.
 *
 * Anything shorter than 10 digits is left unidentified rather than compared:
 * a 7-digit local number says nothing about which area code it belongs to, and
 * matching two of them would merge carriers that share nothing.
 *
 * @param {string} phone
 * @returns {string|null} the value stored in Carrier.normalizedPhone
 */
export const normalizeCarrierPhone = (phone) => {
  const digits = String(phone ?? "").replace(/\D/g, "");
  const national =
    digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  return national.length >= 10 ? national : null;
};

/**
 * Business names have no normalised column — they are compared derived, since
 * a yard renaming a carrier must not have to rewrite a stored key.
 *
 * @param {string} businessName
 * @returns {string|null}
 */
export const normalizeCarrierBusinessName = (businessName) =>
  String(businessName ?? "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase() || null;

/**
 * The regulatory numbers a truck carrier can be identified by, per country.
 *
 * The table lives here rather than in utils/constants because normalisation and
 * matching read it: whether a scheme is numeric decides how a typed value is
 * reduced, and the shape decides whether a mistyped one is called out. The forms
 * take their dropdown choices from the same rows, so a new country is one entry.
 *
 * No scheme is required and none is global. USDOT comes first because it is the
 * closest thing to universal for a US transload: FMCSA requires one of every
 * carrier operating in the US, Canadian and Mexican ones included, so a
 * cross-border carrier already has a USDOT number whatever else it holds.
 *
 * `numeric` schemes are reduced to their digits, which also drops a value typed
 * with its own label ("USDOT 123456", "MC-98765"). The rest keep every
 * alphanumeric character — a Mexican RFC may legitimately contain "&" or "Ñ" —
 * and lose only separators and case.
 *
 * `shape` is deliberately loose, in the spirit of the import's email check: it
 * catches a phone number pasted into a USDOT field, not every invalid number an
 * authority would reject. A value that fails it is reported to the user, never
 * silently dropped.
 */
export const CARRIER_ID_SCHEMES = [
  {
    id: "USDOT",
    name: "USDOT Number",
    label: "USDOT number",
    country: "US",
    numeric: true,
    shape: /^\d{1,8}$/,
    hint: "1–8 digits, e.g. 1523020",
  },
  {
    id: "MC",
    name: "MC / Docket Number",
    label: "MC number",
    country: "US",
    numeric: true,
    shape: /^\d{1,8}$/,
    hint: "Interstate for-hire authority, US and Canadian carriers",
  },
  {
    id: "MX",
    name: "MX Docket Number",
    label: "MX docket number",
    country: "US",
    numeric: true,
    shape: /^\d{1,8}$/,
    hint: "FMCSA docket issued to Mexico-domiciled carriers",
  },
  {
    id: "NSC",
    name: "NSC Number",
    label: "NSC number",
    country: "CA",
    numeric: false,
    shape: /^[A-Z0-9]{4,15}$/,
    hint: "National Safety Code, issued by the province of domicile",
  },
  {
    id: "CVOR",
    name: "CVOR Number",
    label: "CVOR number",
    country: "CA",
    numeric: true,
    shape: /^\d{6,9}$/,
    hint: "Ontario Commercial Vehicle Operator's Registration",
  },
  {
    id: "RFC",
    name: "RFC",
    label: "RFC",
    country: "MX",
    numeric: false,
    shape: /^[A-ZÑ&0-9]{12,13}$/,
    hint: "Registro Federal de Contribuyentes — 12 characters for a company",
  },
  {
    id: "SICT",
    name: "SICT / SCT Permit",
    label: "SICT permit number",
    country: "MX",
    numeric: false,
    shape: /^[A-Z0-9]{4,20}$/,
    hint: "Federal transport permit (formerly SCT)",
  },
  {
    id: "SCAC",
    name: "SCAC Code",
    label: "SCAC code",
    country: null,
    numeric: false,
    shape: /^[A-Z]{2,4}$/,
    hint: "NMFTA code used in EDI — reassignable, so not identity on its own",
  },
  {
    id: "DUNS",
    name: "D-U-N-S Number",
    label: "D-U-N-S number",
    country: null,
    numeric: true,
    shape: /^\d{9}$/,
    hint: "9 digits — a business directory key, not an operating authority",
  },
];

const schemeOf = (scheme) =>
  CARRIER_ID_SCHEMES.find(
    (entry) => entry.id === String(scheme ?? "").trim().toUpperCase(),
  ) ?? null;

/** The dropdown choices the carrier forms offer, in table order. */
export const CARRIER_ID_SCHEME_CHOICES = CARRIER_ID_SCHEMES.map(
  ({ id, name }) => ({ id, name }),
);

/**
 * How a scheme is named back to the user, for a scheme the table knows and for
 * one it does not — a row written by a later build must still describe itself.
 *
 * @param {string} scheme
 * @returns {string}
 */
export const carrierIdSchemeLabel = (scheme) =>
  schemeOf(scheme)?.label ?? `${String(scheme ?? "").trim().toUpperCase()} number`;

/**
 * The value to store for one identifier: case folded, separators gone, and
 * reduced to digits for the numeric schemes.
 *
 * @param {string} scheme
 * @param {string} value
 * @returns {string|null} null when nothing identifying is left
 */
export const normalizeCarrierIdentifierValue = (scheme, value) => {
  const cleaned = String(value ?? "")
    .toUpperCase()
    .replace(/[\s.\-_/#()]/g, "");
  const entry = schemeOf(scheme);
  const reduced = entry?.numeric ? cleaned.replace(/\D/g, "") : cleaned;
  return reduced || null;
};

/**
 * Whether a stored identifier looks like the kind of number its scheme issues.
 *
 * @param {{scheme: string, value: string}} identifier
 * @returns {string|null} the complaint, or null when the value is plausible
 */
export const validateCarrierIdentifier = (identifier) => {
  const entry = schemeOf(identifier?.scheme);
  if (!entry) return null; // an unknown scheme has no shape to hold it to
  const value = normalizeCarrierIdentifierValue(entry.id, identifier?.value);
  if (!value) return null; // blank is "not given", which is always allowed
  return entry.shape.test(value) ? null : `${entry.name}: ${entry.hint}`;
};

/**
 * The identifiers to store alongside a carrier: normalised, blanks dropped, and
 * one row per scheme.
 *
 * A second row for a scheme the carrier already has is dropped rather than kept:
 * two USDOT numbers on one record would make the record match — and veto —
 * itself, and there is no operating entity that has two.
 *
 * Anything other than an array comes back undefined rather than empty, so a form
 * that never showed the field cannot clear a carrier's numbers by saving.
 *
 * @param {object[]} identifiers - raw {scheme, value} rows from a form or record
 * @returns {object[]|undefined}
 */
export const normalizeCarrierIdentifiers = (identifiers) => {
  if (!Array.isArray(identifiers)) return undefined;

  const seen = new Set();
  return identifiers.reduce((acc, identifier) => {
    const scheme = String(identifier?.scheme ?? "").trim().toUpperCase();
    const value = normalizeCarrierIdentifierValue(scheme, identifier?.value);
    if (!scheme || !value || seen.has(scheme)) return acc;
    seen.add(scheme);
    acc.push({ scheme, value });
    return acc;
  }, []);
};

/**
 * The comparison form of one identifier's value.
 *
 * Separate from the stored form because leading zeros are presentation on a
 * numeric number and identity nowhere: a USDOT typed "0123456" is the same
 * authority as "123456", while a D-U-N-S number is conventionally written
 * zero-padded to nine digits and should be shown that way.
 *
 * Exported for the drift test only. It is also what handleCarrierChange keys a
 * CarrierIdentifierClaim by, so the two copies of this reduction have to agree
 * exactly: a difference would let the same number be claimed under two keys and
 * split one company in half.
 */
export const comparableIdentifierValue = (scheme, value) => {
  const normalized = normalizeCarrierIdentifierValue(scheme, value);
  if (!normalized) return null;
  return schemeOf(scheme)?.numeric
    ? normalized.replace(/^0+/, "") || "0"
    : normalized;
};

/**
 * A carrier's identifiers as scheme -> comparable value.
 *
 * @param {object} carrier - form values or a carrier record
 * @returns {Map<string, string>}
 */
const identifierMap = (carrier) => {
  const map = new Map();
  (Array.isArray(carrier?.identifiers) ? carrier.identifiers : []).forEach(
    (identifier) => {
      const scheme = String(identifier?.scheme ?? "").trim().toUpperCase();
      const value = comparableIdentifierValue(scheme, identifier?.value);
      // First row wins, matching normalizeCarrierIdentifiers, so a record
      // holding two rows for one scheme is read the same way it is written.
      if (scheme && value && !map.has(scheme)) map.set(scheme, value);
    },
  );
  return map;
};

/**
 * Whether a save changes any of the carrier's numbers.
 *
 * Compared as sets of comparable values, so reformatting one ("MC-98765" for
 * "98765") is not a change, and neither is reordering the rows. A form that
 * never showed the field submits nothing, which reads as unchanged rather than
 * as a clearing.
 *
 * @param {object} values - the submitted form values
 * @param {object} current - the record as stored
 * @returns {boolean}
 */
const identifiersMoved = (values, current) => {
  if (!Array.isArray(values?.identifiers)) return false;
  const wanted = identifierMap(values);
  const held = identifierMap(current);
  if (wanted.size !== held.size) return true;
  for (const [scheme, value] of wanted) {
    if (held.get(scheme) !== value) return true;
  }
  return false;
};

/**
 * The schemes of `wanted`, strongest authority first.
 *
 * CARRIER_ID_SCHEMES is written in that order — an operating authority above a
 * directory key — so the table's own order is the ranking. A scheme the table
 * does not know sorts last, keeping the order it was entered in: a number from a
 * later build is still worth reporting, but not over one whose weight is known.
 */
const orderedSchemes = (wanted) =>
  [...wanted.keys()]
    .map((scheme, entered) => ({
      scheme,
      rank: CARRIER_ID_SCHEMES.findIndex((entry) => entry.id === scheme),
      entered,
    }))
    .sort(
      (a, b) =>
        (a.rank < 0) - (b.rank < 0) ||
        (a.rank < 0 ? a.entered - b.entered : a.rank - b.rank),
    )
    .map(({ scheme }) => scheme);

/**
 * The carrier in `candidates` that agrees with `wanted` on its strongest shared
 * number — they are the same company.
 *
 * By scheme across every candidate rather than candidate by candidate, for the
 * same reason the weaker checks below are: when one carrier agrees on a D-U-N-S
 * number and another on a USDOT number, the answer has to be the authority, not
 * whichever the index happened to return first.
 *
 * @returns {{scheme: string, carrier: object}|null}
 */
const agreeingCarrier = (wanted, candidateMaps) => {
  for (const scheme of orderedSchemes(wanted)) {
    const value = wanted.get(scheme);
    const hit = candidateMaps.find(([, held]) => held.get(scheme) === value);
    if (hit) return { scheme, carrier: hit[0] };
  }
  return null;
};

/**
 * Whether two carriers share a scheme and hold different numbers under it.
 *
 * This is the veto. Two entries that disagree on an authority's own number are
 * different operating entities, and a weaker signal may not overrule that: two
 * carriers in two states can legitimately share a name, and a shared dispatch
 * line says nothing — an office can answer for more than one operator.
 *
 * The company's email address is the exception, and is checked ahead of the veto.
 * It is not a weaker signal about the company: it names one login, the pool takes
 * it as the username, and a person holds one of those and dispatches for one
 * company. Two entries under one address are therefore the same company however
 * far apart their numbers are — one of the numbers is wrong.
 *
 * The cost, accepted deliberately: a mistyped number on an existing record makes
 * the two entries disagree, so the duplicate it should have caught gets in. A
 * typo is correctable; a wrong merge fuses two companies' contacts.
 */
const identifiersDisagree = (wanted, candidate) => {
  for (const [scheme, value] of wanted) {
    const held = candidate.get(scheme);
    if (held && held !== value) return true;
  }
  return false;
};

/**
 * The normalised columns to write alongside a carrier's raw email and phone.
 *
 * Every path that writes either raw field spreads this into its input, so the
 * stored values can never drift from the values they are derived from.
 *
 * @param {object} carrier - form values or a carrier record
 * @returns {{normalizedEmail: string|null, normalizedPhone: string|null}}
 */
export const buildCarrierIdentityFields = (carrier) => ({
  normalizedEmail: normalizeCarrierEmail(carrier?.email),
  normalizedPhone: normalizeCarrierPhone(carrier?.phone),
});

/**
 * The identity fields an edit actually moves.
 *
 * A carrier that predates the check can already be sharing an email with
 * another record, and the edit form cannot even change an email — checking
 * every field on every save would leave such a pair permanently unsaveable over
 * a collision the user did not create and cannot undo from that form. Checking
 * only what moved blocks the edit that introduces a duplicate and lets every
 * other one through.
 *
 * @param {object} values - the submitted form values
 * @param {object} current - the record as stored
 * @returns {object} the subset of {email, phone, businessName} that changed
 */
export const changedCarrierIdentity = (values, current) => {
  const changed = {};

  if (
    normalizeCarrierEmail(values?.email) !==
    normalizeCarrierEmail(current?.email)
  )
    changed.email = values?.email;

  if (
    normalizeCarrierPhone(values?.phone) !==
    normalizeCarrierPhone(current?.phone)
  )
    changed.phone = values?.phone;

  if (
    normalizeCarrierBusinessName(values?.businessName) !==
    normalizeCarrierBusinessName(current?.businessName)
  )
    changed.businessName = values?.businessName;

  // Both callers re-supply the numbers on top of this result — they ride along
  // whether or not they moved, being what vetoes a weaker match. What is not
  // redundant is the *key*: a caller skips the duplicate lookup entirely when
  // nothing moved, so a save that changed only a number has to be reported here
  // or it goes out unchecked.
  if (identifiersMoved(values, current)) changed.identifiers = values?.identifiers;

  return changed;
};

// Stored normalised values are preferred, but a carrier created before those
// columns existed has none — deriving from the raw field brings the whole
// existing book into the check without a backfill.
const identityEmail = (carrier) =>
  carrier?.normalizedEmail || normalizeCarrierEmail(carrier?.email);

const identityPhone = (carrier) =>
  carrier?.normalizedPhone || normalizeCarrierPhone(carrier?.phone);

const identityBusinessName = (carrier) =>
  normalizeCarrierBusinessName(carrier?.businessName);

/**
 * The first carrier in `carriers` that is the same company as `values`.
 *
 * Pure, so the rules can be tested without a network. A field the form left
 * blank — or a phone too short to identify a line — is skipped rather than
 * matched against other blanks.
 *
 * Identity is decided in three passes. A shared regulatory number is the whole
 * answer, so it is looked for across every candidate first. The company's email
 * address is next, and answers on its own: it is the address the primary dispatch
 * login is read off, one person holds one login, and a person dispatches for one
 * company — so two entries sharing it are the same company whatever their numbers
 * say. Only then are the candidates whose numbers *contradict* the ones being
 * entered dropped, because neither a name nor a phone line may overrule an
 * authority: what is left is compared on those two weaker signals as before.
 *
 * @param {object[]} carriers - the yard's carriers, active and archived
 * @param {object} values - the carrier being created or edited
 * @param {string} [excludeId] - the record being edited, which is not its own duplicate
 * @returns {{field: string, carrier: object, scheme?: string}|null}
 */
export const findCarrierConflict = (carriers, values, excludeId) => {
  const allCandidates = (carriers ?? []).filter(
    (carrier) => carrier && carrier.id !== excludeId,
  );

  const wantedIdentifiers = identifierMap(values);

  // Mapped once: both passes below read every candidate's numbers, and the
  // normalisation behind each map is not free.
  const candidateMaps =
    wantedIdentifiers.size > 0
      ? allCandidates.map((candidate) => [candidate, identifierMap(candidate)])
      : [];

  if (wantedIdentifiers.size > 0) {
    const agreed = agreeingCarrier(wantedIdentifiers, candidateMaps);
    if (agreed) return { field: "identifier", ...agreed };
  }

  // Ahead of the veto: an address is one login and one login is one company, so
  // no disagreeing number can explain two entries sharing one. Reported as
  // "email" rather than as a number problem because the address is the field the
  // user can see and change.
  const wantedEmail = normalizeCarrierEmail(values?.email);
  if (wantedEmail) {
    const carrier = allCandidates.find(
      (candidate) => identityEmail(candidate) === wantedEmail,
    );
    if (carrier) return { field: "email", carrier };
  }

  const candidates =
    wantedIdentifiers.size > 0
      ? candidateMaps
          .filter(([, held]) => !identifiersDisagree(wantedIdentifiers, held))
          .map(([candidate]) => candidate)
      : allCandidates;

  // Field by field rather than carrier by carrier: when two carriers each
  // collide on a different field, the one reported is the strongest identity
  // rather than whichever the index happened to return first.
  const checks = [
    {
      field: "phone",
      wanted: normalizeCarrierPhone(values?.phone),
      of: identityPhone,
    },
    {
      field: "businessName",
      wanted: normalizeCarrierBusinessName(values?.businessName),
      of: identityBusinessName,
    },
  ];

  for (const { field, wanted, of } of checks) {
    if (!wanted) continue;
    const carrier = candidates.find((candidate) => of(candidate) === wanted);
    if (carrier) return { field, carrier };
  }

  return null;
};

/**
 * What to tell the user about a conflict.
 *
 * An archived carrier gets its own wording: the record is invisible in the
 * Carriers list, so "a carrier already uses this email" reads as a bug unless it
 * also says where the carrier went and what to do instead.
 *
 * @param {{field: string, carrier: object, scheme?: string}} conflict - a
 *   findCarrierConflict result
 * @returns {string}
 */
export const describeCarrierConflict = ({ field, carrier, scheme }) => {
  const label =
    field === "identifier"
      ? carrierIdSchemeLabel(scheme)
      : (CONFLICT_LABELS[field] ?? field);
  const archived = carrier?.active === "false";
  const subject = carrier?.businessName
    ? `${archived ? "Archived carrier" : "Carrier"} "${carrier.businessName}"`
    : `Another ${archived ? "archived " : ""}carrier`;

  return archived
    ? `${subject} already uses this ${label}. Restore it from the Carriers list instead of creating a new carrier.`
    : `${subject} already uses this ${label}.`;
};

// One page holds every carrier of all but the largest yards; the loop is what
// keeps the check honest for the rest, since a duplicate sitting on page two is
// the same duplicate.
const IDENTITY_PAGE_LIMIT = 1000;

/**
 * Every carrier in the yard, reduced to the fields the check compares.
 *
 * Fetched fresh rather than read from carrierListCache: that cache holds active
 * carriers only, and is deliberately stale so dropdowns stay fast.
 *
 * @param {string} reloadYardId
 * @returns {Promise<object[]>}
 */
export const fetchCarrierIdentities = async (reloadYardId) => {
  const items = [];
  let nextToken;

  do {
    // eslint-disable-next-line no-await-in-loop
    const response = await API.graphql(
      graphqlOperation(carrierIdentitiesByReloadYard, {
        reloadYardId,
        limit: IDENTITY_PAGE_LIMIT,
        nextToken,
      }),
    );
    const page = response?.data?.carrierByActiveByName;
    if (page?.items?.length) items.push(...page.items.filter(Boolean));
    nextToken = page?.nextToken;
  } while (nextToken);

  return items;
};

/**
 * Whether a carrier can be saved, or the reason it cannot.
 *
 * A lookup that fails blocks the save. The check it replaced swallowed its own
 * errors and returned "unique", which turned a timeout into a duplicate carrier
 * — the one outcome the check exists to prevent. Being told to try again is the
 * cheaper failure.
 *
 * @param {object} values - the carrier being created or edited
 * @param {object} [options]
 * @param {string} [options.excludeId] - the record being edited
 * @param {string} [options.reloadYardId] - defaults to the signed-in user's yard
 * @returns {Promise<{message: string, conflict?: object}|null>} null when the
 *   carrier is safe to save
 */
export const validateCarrierUnicity = async (values, options = {}) => {
  const { excludeId, reloadYardId = window.appUser?.reloadYardId } = options;

  if (!reloadYardId) {
    logError(
      "utils:carrierIdentity:validateCarrierUnicity",
      new Error("No reloadYardId to check carrier uniqueness against"),
      false,
    );
    return {
      message: "Could not check for an existing carrier. Please try again.",
    };
  }

  let carriers;
  try {
    carriers = await fetchCarrierIdentities(reloadYardId);
  } catch (error) {
    logError("utils:carrierIdentity:validateCarrierUnicity", error, false);
    return {
      message: "Could not check for an existing carrier. Please try again.",
    };
  }

  const conflict = findCarrierConflict(carriers, values, excludeId);
  return conflict
    ? { message: describeCarrierConflict(conflict), conflict }
    : null;
};

// How long the on-blur checks may reuse one fetch of the yard's book.
//
// Filling in a carrier blurs three fields, and blurs them again every time the
// user goes back to correct one. A fetch apiece would be the same paginated
// scan of the same rows, over and over, while the form is still being typed.
// Being a little stale costs nothing here: validateCarrierUnicity re-checks
// against a fresh fetch on save, so a carrier another user added mid-form still
// blocks the save.
const IDENTITY_CACHE_MS = 30000;

let identityCache = null;

const cachedCarrierIdentities = (reloadYardId) => {
  const fresh =
    identityCache?.reloadYardId === reloadYardId &&
    Date.now() - identityCache.at < IDENTITY_CACHE_MS;

  if (!fresh) {
    const entry = {
      reloadYardId,
      at: Date.now(),
      promise: fetchCarrierIdentities(reloadYardId).catch((error) => {
        // Never cache a failure — the next blur should retry. Guarded, so a
        // later fetch that already replaced this entry is left alone.
        if (identityCache === entry) identityCache = null;
        throw error;
      }),
    };
    identityCache = entry;
  }

  return identityCache.promise;
};

/**
 * Forget the cached book. Call it from every path that creates a carrier, so
 * the next form's blur check knows about the carrier just created rather than
 * waiting out the window.
 */
export const clearCarrierIdentityCache = () => {
  identityCache = null;
};

/**
 * Whether one field of the carrier being entered already belongs to another
 * carrier in the yard — the check behind the create forms' on-blur validation.
 *
 * Unlike validateCarrierUnicity, a failed lookup is reported as nothing to say
 * rather than as a problem. A blur is not a save, and "could not check" on a
 * field the user is still filling in names nothing they can act on. The save is
 * where the check has to hold, and it fetches fresh and blocks on its own
 * errors.
 *
 * @param {string} field - "businessName", "email" or "phone"
 * @param {string} value - what the user typed
 * @param {object} [options]
 * @param {string} [options.excludeId] - the record being edited
 * @param {string} [options.reloadYardId] - defaults to the signed-in user's yard
 * @param {object[]} [options.identifiers] - the numbers the form holds so far,
 *   carried so a number already entered can answer first: a shared authority is
 *   identity, and it outranks whichever field the user just left.
 * @returns {Promise<{message: string, conflict: object}|null>} null when the
 *   field is free to use
 */
export const checkCarrierFieldUnicity = async (field, value, options = {}) => {
  const {
    excludeId,
    identifiers,
    reloadYardId = window.appUser?.reloadYardId,
  } = options;

  if (!reloadYardId || !value) return null;

  let carriers;
  try {
    carriers = await cachedCarrierIdentities(reloadYardId);
  } catch (error) {
    logError("utils:carrierIdentity:checkCarrierFieldUnicity", error, false);
    return null;
  }

  // One field at a time: findCarrierConflict skips whatever it is not given, so
  // the answer is about the field the user just left rather than the whole form.
  // "identifier" is the numbers themselves being checked; every other field
  // carries them along only so the veto applies.
  const probe =
    field === "identifier"
      ? { identifiers: value }
      : { [field]: value, identifiers };

  // Two passes, because at a blur the numbers in the form are whatever the user
  // has typed so far. A USDOT number is 1 to 8 digits, so a half-typed one is
  // indistinguishable from a finished one — and letting it veto would make every
  // carrier holding that authority drop out of the candidates, silently turning
  // off the email, phone and name warnings for the rest of the form.
  //
  // So the veto decides which answer is *reported first*, never whether the user
  // is told anything. The save is where the veto has to hold, and it fetches
  // fresh and refuses on its own.
  const conflict =
    findCarrierConflict(carriers, probe, excludeId) ??
    (field === "identifier"
      ? null
      : findCarrierConflict(carriers, { [field]: value }, excludeId));

  // The answer has to belong to the field the user just left — with one
  // exception. A blur on any field can turn up an identifier conflict, because
  // the numbers were already in the form and outrank whatever was typed; the
  // numbers have no blur of their own to report it on, and dropping it would
  // hide the strongest duplicate signal in the form until the save. So it is
  // shown wherever it was found: the message names the number, not the field.
  if (!conflict) return null;
  if (conflict.field !== field && conflict.field !== "identifier") return null;

  return { message: describeCarrierConflict(conflict), conflict };
};
